import { observer } from 'mobx-react-lite'
import { Image, StyleSheet, useWindowDimensions, View, ViewStyle } from 'react-native'
import { Pot } from '@ppwr/poker'
import { useStore } from '../../../models'
import { color, fontFamily } from '../../../theme'
import { Button } from '../Button'
import { Card } from '../Card'
import { ChipsLayout } from './ChipsLayout'
import { SeatedObserver, SeatedObserverContainer } from './SeatedObserver'
import { useTableTourPositioning } from '../../../hooks/usePositioning'
import { DS } from '../../../design-system'
import { numberWithCommas } from '../../../utils/numberWithCommas'
import { ChipsWaitingGameLayout } from './ChipsWaitingGame'
import { RoundWinner } from '../ModalContent/Content'
import { HandWinner } from '../HandWinner'
import { useResponsive } from '@/screens/new-design/hooks'
import { PokerCard } from '@/components/GameAssets/Cards/PokerCard'
import { Suit, Value } from 'pokersolver'
import { useMemo } from 'react'
import { useGameBottomModal } from '@/screens/new-design/hooks/useGameBottomModals'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { BETTING_CHIP_SIZE } from '@/components/ANewDesign/PlayerBettingChips'
import { isWeb } from '@/components/WebWrapper'
interface BoardProps {
  onSidePotsPress: (pots: Pot[]) => void
}

const carret = {
  up: require('../../../../assets/sidepots/up.png'),
  down: require('../../../../assets/sidepots/down.png')
}

export const ThreeCardAmount = 1
export const TexasHoldEmAmount = 5

export const Board = observer(({ onSidePotsPress }: BoardProps) => {
  const {
    game: {
      communityCards,
      mainPot,
      seatedObserver,
      sidePots,
      showSidePots,
      isThreeCard,
      isDealTheFlop,
      isTableTour,
      gameStartTime,
      isRaiseOrFold,
      winners,
      isHandWinnersEvent,
      isGameOver,
      step,
      isGamePaused
    }
  } = useStore()
  const { verticalScale } = useResponsive()
  const { ref, onLayout } = useTableTourPositioning({ key: 'pot', shouldSave: () => true })
  const amountOfCardsMissing = (isThreeCard ? ThreeCardAmount : TexasHoldEmAmount) - (communityCards?.length ?? 0)
  const boardCards = [...communityCards, ...Array.from({ length: amountOfCardsMissing }).map(() => undefined)]

  const { height: HEIGHT } = useWindowDimensions()
  const isSmallerDevice = HEIGHT <= 667
  const isXSmallerDevice = HEIGHT <= 640

  const DEAL_THE_FLOP_POT_VALUE = 10000
  const handleSidePotsPress = () => onSidePotsPress(sidePots)

  const getBoardPotText = () => {
    if (isTableTour || isRaiseOrFold) {
      return 'Pot'
    }
    if (isDealTheFlop) return numberWithCommas(DEAL_THE_FLOP_POT_VALUE)
    return numberWithCommas(mainPot ?? 0)
  }
  const showPot = isTableTour || isRaiseOrFold || isDealTheFlop || gameStartTime > 0
  const showSeatedObserver = seatedObserver && !isHandWinnersEvent && !isGameOver
  const tightUi = isXSmallerDevice && showSeatedObserver

  //TODO: this smaller dimensions check should be handled gracefully isSmallerDevice

  return (
    <View
      style={[
        styles.container,
        {
          paddingBottom: verticalScale(20) * 3
        },
        isRaiseOrFold ? { minWidth: 145 } : {},
        isGamePaused
          ? { backgroundColor: `${DS.palette['Shades/Ivory Cream']}26` }
          : { backgroundColor: DS.palette['Shades/Plum Wine'] }
      ]}
    >
      {showSeatedObserver ? (
        <View style={[styles.seatedObserver, { marginTop: isSmallerDevice ? -25 : -10 }]}>
          <SeatedObserverContainer />
        </View>
      ) : null}
      <View style={[styles.contentContainer, { gap: isSmallerDevice ? 10 : 30 }]}>
        {showPot ? (
          <View
            ref={ref}
            onLayout={onLayout}
            style={[styles.chipsContainer, { opacity: isTableTour && step === 11 ? 0 : undefined }]}
            collapsable={false}
          >
            {isGameOver ? <HandWinner winnerCardSizes={16} /> : <RoundWinner />}
            {!isGameOver ? (
              <>
                <ChipsLayout />
                <DS.Text variant="Heading 4/Regular" color="Brand/Cream" style={{ marginTop: tightUi ? -15 : 0 }}>
                  {getBoardPotText()}
                </DS.Text>
              </>
            ) : null}
          </View>
        ) : (
          <ChipsWaitingGameLayout hideChips={tightUi} />
        )}

        {!(isRaiseOrFold || isGameOver) ? (
          <View style={{ gap: 4 }}>
            <View style={styles.cardsContainer}>
              {boardCards.slice(0, 3).map((card, i) => (
                <Card
                  key={`board-card-top-${card}-${i}`}
                  card={card}
                  //TODO: should we use winners?.[0]?.winningCards ? or the difference like before https://github.com/Poker-Powher/poker-power/blob/a564d2f83a862f9f55b367c70ce0e335faf8f7c7/apps/poker-power/src/app/components/ANewDesign/HandWinner/HandWinner.tsx#L80
                  disabled={
                    card &&
                    isHandWinnersEvent &&
                    winners?.[0]?.winningCards?.length > 0 &&
                    !winners?.[0]?.winningCards?.includes(card)
                  }
                  isOnTable
                  fontSize={verticalScale(19)}
                />
              ))}
            </View>
            <View style={styles.cardsContainer}>
              {boardCards.slice(3, 5).map((card, i) => (
                <Card
                  key={`board-card-bottom-${card}-${i}`}
                  card={card}
                  //TODO: should we use winners?.[0]?.winningCards ? or the difference like before https://github.com/Poker-Powher/poker-power/blob/a564d2f83a862f9f55b367c70ce0e335faf8f7c7/apps/poker-power/src/app/components/ANewDesign/HandWinner/HandWinner.tsx#L80
                  disabled={
                    card &&
                    isHandWinnersEvent &&
                    winners?.[0]?.winningCards?.length > 0 &&
                    !winners?.[0]?.winningCards?.includes(card)
                  }
                  isOnTable
                  fontSize={verticalScale(19)}
                />
              ))}
            </View>
          </View>
        ) : null}
        {sidePots.length && !isHandWinnersEvent ? (
          <View style={styles.buttonContainer}>
            <Button
              textStyle={[styles.sidePotsText]}
              text="Side Pots"
              onPress={handleSidePotsPress}
              size="small"
              type="simple"
              containerStyle={[{ paddingHorizontal: 8 }]}
            />
            <Image style={styles.carrortIcon} source={showSidePots ? carret.up : carret.down} />
          </View>
        ) : null}
      </View>
    </View>
  )
})

// Design aspect ratios
// Card: window width = 402 / card width = 38  >> 10.5
// Table: window width = 402 / card width = 146 >> 2.75
export const BoardLayoutConstants = {
  getCardWidth: (windowWidth: number) => {
    return Math.round(windowWidth / 10.5)
  },
  getCardHeight: (windowHeight: number) => {
    return Math.round(windowHeight / 13.5)
  },
  getTableWidth: (windowWidth: number) => {
    return Math.round(windowWidth / 2.75)
  },
  getTableHeight: (windowWidth: number) => {
    return Math.round(windowWidth / 2.75)
  }
}

const BoardHeader = ({ children, ...props }: React.ComponentProps<typeof View>) => {
  return (
    <View
      {...props}
      style={[
        {
          minHeight: 44
        },
        props.style
      ]}
    >
      {children}
    </View>
  )
}

const BoardContent = ({ children, ...props }: React.ComponentProps<typeof View>) => {
  return (
    <View
      {...props}
      style={[
        {
          flexGrow: 1,
          justifyContent: 'center',
          gap: 24
        },
        props.style
      ]}
    >
      {children}
    </View>
  )
}

const BoardFooter = ({ children, ...props }: React.ComponentProps<typeof View>) => {
  return (
    <View
      {...props}
      style={[
        {
          marginTop: 'auto'
        },
        props.style
      ]}
    >
      {children}
    </View>
  )
}

type BoardBaseProps = {
  maxCards?: number
  cards: string[]
  winnerCards?: string[]
  potText: string
  isGameOver: boolean
  showCards?: boolean
  showPot: boolean
  showSidePots?: boolean
  //
  headerComponent?: React.ReactNode
  footerComponent?: React.ReactNode
  //
  containerStyle?: ViewStyle
  potContainerStyle?: ViewStyle
}
export const BoardBase = ({
  maxCards = 5,
  cards = [],
  winnerCards = [],
  potText,
  isGameOver,
  showCards = true,
  showPot,
  headerComponent = null,
  footerComponent = null,
  containerStyle,
  potContainerStyle
}: BoardBaseProps) => {
  const { verticalScale } = useResponsive()

  const { ref, onLayout } = useTableTourPositioning({ key: 'pot', shouldSave: () => true })

  const insets = useSafeAreaInsets()
  const windowSize = useWindowDimensions()

  const usableHight = windowSize.height - insets.top - insets.bottom
  const contentGap = usableHight < 700 ? 15 : 24

  const cardWidth = isWeb ? 38 : BoardLayoutConstants.getCardWidth(windowSize.width)
  const tableWidth = BoardLayoutConstants.getTableWidth(windowSize.width)

  const tableBorderSize = verticalScale(BETTING_CHIP_SIZE)

  return (
    <View
      style={[
        {
          borderRadius: 100,
          flex: 1,
          height: '100%',
          width: tableWidth + tableBorderSize,
          maxWidth: 180,
          // borderColor: 'rgba(0,0,0,.05)',
          borderColor: 'rgba(0,0,0,0)',
          borderWidth: tableBorderSize,
          alignItems: 'center'
        },
        {
          paddingBottom: tableBorderSize * 3
        },
        containerStyle
      ]}
    >
      <BoardHeader>
        <View style={{ marginTop: -tableBorderSize }}>{headerComponent}</View>
      </BoardHeader>

      <BoardContent style={{ gap: contentGap }}>
        {showPot ? (
          <View ref={ref} onLayout={onLayout} style={[styles.chipsContainer, potContainerStyle]} collapsable={false}>
            {isGameOver ? <HandWinner winnerCardSizes={16} /> : <RoundWinner />}
            {!isGameOver ? (
              <>
                <ChipsLayout />
                <DS.Text variant="Heading 4/Regular" color="Brand/Cream" style={{ marginTop: 0 }}>
                  {potText}
                </DS.Text>
              </>
            ) : null}
          </View>
        ) : (
          <ChipsWaitingGameLayout />
        )}

        {showCards && (
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              flexWrap: 'wrap',
              maxWidth: '100%',
              gap: 4
            }}
          >
            {Array.from({ length: maxCards }).map((_, i, { length }) => {
              const isCardDisabled = cards[i] && winnerCards.length > 0 && !winnerCards.includes(cards[i])
              // first card, second-last and last
              const soundOnIndex = [0, length - 2, length - 1]
              const isSoundEnabled = soundOnIndex.includes(i)
              return (
                <PokerCard.Flip
                  key={`board-card-flippable-${i}`}
                  suit={cards[i]?.[1] as Suit | undefined}
                  value={cards[i]?.[0] as Value | undefined}
                  side={cards[i] ? 'front' : 'back'}
                  soundEnable={isSoundEnabled}
                  size={cardWidth}
                  containerStyle={{
                    opacity: isCardDisabled ? 0.6 : 1
                  }}
                />
              )
            })}
          </View>
        )}
      </BoardContent>

      <BoardFooter>{footerComponent}</BoardFooter>
    </View>
  )
}

export const BoardContainer = observer(() => {
  const {
    game: {
      communityCards,
      mainPot,
      seatedObserver,
      sidePots,
      showSidePots: showSidePotsOpen,
      isThreeCard,
      isDealTheFlop,
      isTableTour,
      gameStartTime,
      isRaiseOrFold,
      winners,
      isHandWinnersEvent,
      isGameOver,
      step,
      isGamePaused
    }
  } = useStore()

  const winnerCards = isHandWinnersEvent ? winners?.[0]?.winningCards : []

  const { handleSidePotsPress } = useGameBottomModal()

  const boardPotText = useMemo(() => {
    if (isTableTour || isRaiseOrFold) {
      return 'Pot'
    }
    if (isDealTheFlop) {
      const DEAL_THE_FLOP_POT_VALUE = 10000
      return numberWithCommas(DEAL_THE_FLOP_POT_VALUE)
    }
    return numberWithCommas(mainPot ?? 0)
  }, [isTableTour, isRaiseOrFold, isDealTheFlop, mainPot])

  const showPot = isTableTour || isRaiseOrFold || isDealTheFlop || gameStartTime > 0
  const showSeatedObserver = seatedObserver && !isHandWinnersEvent && !isGameOver

  const showSidePots = Boolean(sidePots.length && !isHandWinnersEvent)

  const headerComponent = useMemo(() => showSeatedObserver && <SeatedObserverContainer />, [showSeatedObserver])

  const footerComponent = useMemo(
    () =>
      showSidePots && (
        <View style={styles.buttonContainer}>
          <Button
            textStyle={[styles.sidePotsText]}
            text="Side Pots"
            onPress={handleSidePotsPress}
            size="small"
            type="simple"
            containerStyle={[{ paddingHorizontal: 8 }]}
          />
          <Image style={styles.carrortIcon} source={showSidePotsOpen ? carret.up : carret.down} />
        </View>
      ),
    [showSidePotsOpen, handleSidePotsPress, showSidePots]
  )

  return (
    <BoardBase
      maxCards={isThreeCard ? ThreeCardAmount : TexasHoldEmAmount}
      cards={communityCards}
      winnerCards={winnerCards}
      showCards={!(isRaiseOrFold || isGameOver)}
      showPot={showPot}
      isGameOver={isGameOver}
      potText={boardPotText}
      potContainerStyle={{
        opacity: isTableTour && step === 11 ? 0 : undefined
      }}
      containerStyle={{
        backgroundColor: isGamePaused ? `${DS.palette['Shades/Ivory Cream']}26` : DS.palette['Shades/Plum Wine']
      }}
      headerComponent={headerComponent}
      footerComponent={footerComponent}
    />
  )
})

const styles = StyleSheet.create({
  container: {
    borderRadius: 100,
    flex: 1,
    justifyContent: 'space-around',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: DS.spacing[8]
  },
  seatedObserver: {
    flex: 0.01
  },
  contentContainer: {
    // flex: 4,
    justifyContent: 'center'
  },
  cardsContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    flexWrap: 'wrap',
    maxWidth: '100%',
    gap: 4
  },
  chipsContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 10
  },
  chipsText: {
    color: 'white',
    fontWeight: '500',
    fontFamily: fontFamily.FeatureDeckBold,
    fontSize: 20
  },
  buttonContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center'
  },
  carrortIcon: {
    height: 17,
    width: 17
  },
  sidePotsText: {
    color: color.palette.white,
    fontWeight: '700',
    fontFamily: fontFamily.GTAmericaExtendedMedium,
    fontSize: 12,
    letterSpacing: 0.2
  }
})
